import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { PrimitiveAtom, useAtom } from "jotai";
import { useMemo, useState } from "react";
import { Checkbox, Color, Flex, Scroller, Search, Select, SubgridTable, Text } from "src/elements";
import { SubgridCell } from "src/elements/SubgridTable/components/SubgridCell";
import { SubgridRowElement } from "src/elements/SubgridTable/components/SubgridRow";
import { calculateSpaceMix } from "../../store/utils";
import { planogramSelectedItemsAtom } from "src/components/PlanogramExtensions/store/atoms";
import { omniAtom } from "src/utils/atoms";
import { lightenDarkenColor } from "src/utils";
import { isPlanogramItemSelection } from "src/components/Planogram/common/planogramSelection";

export type SpaceMixLabel = "CDT0" | "CDT1";

interface Props {
  pogAtom: PrimitiveAtom<Pog>;
  isPog: boolean;
}

const modeAtom = omniAtom<SpaceMixLabel>({
  key: "spaceMix/mode",
  value: "CDT0",
});

const showTargetWidthAtom = omniAtom<boolean>({
  key: "spaceMix/showTargetWidth",
  value: false,
});

const showSalesContributionAtom = omniAtom<boolean>({
  key: "spaceMix/showSalesContribution",
  value: true,
});

export const SpaceMix = ({ pogAtom, isPog }: Props) => {
  const [pog] = useAtom(pogAtom);
  const [mode, setMode] = useAtom(modeAtom);
  const [selectedItems] = useAtom(planogramSelectedItemsAtom);
  const [showTargetWidth, setShowTargetWidth] = useAtom(showTargetWidthAtom);
  const [showSalesContribution, setShowSalesContribution] = useAtom(showSalesContributionAtom);
  const selectedShelfItems = selectedItems.filter(isPlanogramItemSelection);
  const [search, setSearch] = useState("");
  let scrolled = false;

  const spaceMix = useMemo(() => {
    return calculateSpaceMix(pog, isPog, mode);
  }, [pog.containers, pog.unrangedContainers, isPog, pog.planogram.bays, mode]);

  const columns = useMemo(
    () => [
      {
        id: "cdt-label",
        header: mode === "CDT0" ? "CDT0" : "CDT1",
        width: "1fr",
        footer: "TOTAL",
      },
      {
        id: "dimensions",
        header: "Current Width",
        width: "88px",
        footer: `${((spaceMix.total.dimensionsSum * 100) / spaceMix.total.area).toFixed(2)}%`,
      },
      ...(showTargetWidth
        ? [
            {
              id: "sales",
              header: "Target Width",
              width: "88px",
              footer: "100%",
            },
          ]
        : []),
      ...(showSalesContribution
        ? [
            {
              id: "salesAmount",
              header: "Sales %",
              width: "88px",
              footer: spaceMix.total.salesAmountSum > 0 ? "100%" : "0.00%",
            },
          ]
        : []),
    ],
    [mode, spaceMix.total, showTargetWidth, showSalesContribution],
  );

  return (
    <Flex column width="100%" padding="10px" gap="10px">
      <Flex gap="10px">
        {isPog && (
          <Select
            width="min-width"
            title="Label"
            options={
              [
                { label: "CDT0", value: "CDT0" },
                { label: "CDT1", value: "CDT1" },
              ] satisfies { label: string; value: SpaceMixLabel }[]
            }
            value={mode}
            setValue={setMode}
          />
        )}

        <Search search={search} setSearch={setSearch} placeholder="Search variants..." />
      </Flex>

      <Flex gap="10px">
        <Checkbox
          isChecked={showTargetWidth}
          setIsChecked={setShowTargetWidth}
          label="Target Width"
          width="min-content"
          isCheckboxLeft
          size="small"
          name="spacemix-show-target-width"
        />

        <Checkbox
          isChecked={showSalesContribution}
          setIsChecked={setShowSalesContribution}
          label="Sales %"
          width="min-content"
          isCheckboxLeft
          size="small"
          name="spacemix-show-sales-contribution"
        />
      </Flex>

      <Scroller gutter="auto">
        <SubgridTable
          rowRenderer={(row) => {
            const currentWidth = ((row.dimensions * 100) / spaceMix.total.area).toFixed(2);
            const targetWidth = ((row.sales * 100) / spaceMix.total.salesSum).toFixed(2);
            const salesContribution =
              spaceMix.total.salesAmountSum > 0
                ? ((row.salesAmount * 100) / spaceMix.total.salesAmountSum).toFixed(2)
                : "0.00";
            const isInvalid = Number(currentWidth) * 100 < Number(targetWidth) * 100;
            const isCdtSelected = selectedShelfItems.some((item) => {
              const isVariantMatches = item["variant"] === row["cdt0"];
              return mode === "CDT0"
                ? isVariantMatches
                : isVariantMatches && item["cdt1"] === row["cdt1"];
            });

            let assignScroll = false;
            if (isCdtSelected && !scrolled) {
              assignScroll = true;
              scrolled = true;
            }

            const cdt0Color = lightenDarkenColor(Color.textSecondary, 30);
            let cdtLabel: React.ReactNode;

            if (mode === "CDT0") {
              cdtLabel = row.cdt0;
            } else {
              if (row.isCdt1NonUnique) {
                cdtLabel = (
                  <>
                    <Text color={cdt0Color} variant="small2">
                      {row.cdt0}-
                    </Text>
                    {row.cdt1}
                  </>
                );
              } else {
                cdtLabel = row.cdt1;
              }
            }

            return (
              <SubgridRowElement
                ref={(element) => {
                  if (assignScroll) {
                    element?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }}
                border={isCdtSelected ? "2px solid black" : "none"}
                scrollMarginTop="48px"
              >
                <SubgridCell leftBorder background={isInvalid ? Color.redDisabled : undefined}>
                  <Text variant="small2">{cdtLabel}</Text>
                </SubgridCell>

                <SubgridCell background={isInvalid ? Color.redDisabled : undefined}>
                  <Text variant="small2">{currentWidth}%</Text>
                </SubgridCell>

                {showTargetWidth && (
                  <SubgridCell background={isInvalid ? Color.redDisabled : undefined}>
                    <Text variant="small2">{targetWidth}%</Text>
                  </SubgridCell>
                )}

                {showSalesContribution && (
                  <SubgridCell background={isInvalid ? Color.redDisabled : undefined}>
                    <Text variant="small2">{salesContribution}%</Text>
                  </SubgridCell>
                )}
              </SubgridRowElement>
            );
          }}
          columns={columns}
          data={Object.keys(spaceMix.individual)
            .filter((variant) => variant.toLowerCase().includes(search.toLowerCase()))
            .sort((variant1, variant2) =>
              spaceMix.individual[variant1].cdt1Priority <
              spaceMix.individual[variant2].cdt1Priority
                ? -1
                : 1,
            )
            .map((key) => ({
              cdt0: spaceMix.individual[key].cdt0,
              cdt1: spaceMix.individual[key].cdt1,
              dimensions: spaceMix.individual[key].dimensions,
              sales: spaceMix.individual[key].sales,
              salesAmount: spaceMix.individual[key].salesAmount,
              isCdt1NonUnique: spaceMix.individual[key].isCdt1NonUnique === true,
            }))}
        />
      </Scroller>
    </Flex>
  );
};
